import React, { useEffect, useState } from "react";
import Slider from "react-slick";
import { useNavigate } from "react-router-dom";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import Footer from "./Footer";
import "./Home.css";
import {BASE_URL} from "./Constants";

const NextArrow = ({ onClick }) => <div className="arrow next" onClick={onClick}>▶</div>;
const PrevArrow = ({ onClick }) => <div className="arrow prev" onClick={onClick}>◀</div>;

const Home = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("");
  const [wishlistStatus, setWishlistStatus] = useState({});


  // ---------------- Fetch logged-in user ----------------
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const res = await fetch(`${BASE_URL}/get_user_details`, {
          method: "GET",
          credentials: "include", // send HTTP-only cookie
        });
        if (!res.ok) throw new Error("Not logged in");
        const data = await res.json();
        setUser(data); // { user_id, email }
      } catch (err) {
        console.log("No user logged in:", err.message);
        setUser(null);
      }
    };
    fetchUser();
  }, []);

  // ---------------- Fetch categories & featured products ----------------
  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await fetch(`${BASE_URL}/items`, {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json"
          }
        });

        if (!res.ok) {
          console.error("Failed to fetch items:", res.statusText);
          return;
        }

        const data = await res.json();
        const items = Array.isArray(data.items) ? data.items : [];

        // Unique categories
        const uniqueCategories = [
          ...new Map(items.map((p) => [p.category, p])).values(),
        ].map((cat) => ({
          name: cat.category,
          image: cat.images?.length
            ? `${BASE_URL}/download_images/${
                typeof cat.images[0] === "object" ? cat.images[0].id : cat.images[0]
              }`
            : "/assets/default.jpg",
        }));

        setCategories(uniqueCategories);
        setFeaturedProducts(items.slice(0, 4));
      } catch (err) {
        console.error("Error fetching home data:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleAddToWishlist = async (product) => {
  if (!user) {
    setMessage("Please login to add items to wishlist")
    setMessageType("error")
    navigate("/checkout/login");
    return;
  }

  try {
    let wishlistId;
    const resWishlist = await fetch(`${BASE_URL}/wishlist/fetch_wishlist_details?user_id=${user.user_id}`, {
      method: "GET",
      credentials: "include",
    });

    if (resWishlist.status === 404) {
      // Create wishlist for user if it doesn't exist
      const createRes = await fetch(`${BASE_URL}/wishlist/create_wishlist`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ user_id: user.user_id }),
      });
      const data = await createRes.json();
      wishlistId = data.wishlist_id;
    } else {
      const data = await resWishlist.json();
      wishlistId = data.wishlist_id;
    }

    // Handle image URL if product has multiple images
    const imageUrl = product.images?.length
      ? `${BASE_URL}/download_images/${
          typeof product.images[0] === "object" ? product.images[0].id : product.images[0]
        }`
      : "/assets/default.jpg";

      const payload = {
        wishlistId : wishlistId,
        item_id: Number(product.item_id),
        price: product.price,
        image: imageUrl,
      };

      // Add item to backend wishlist
      const resAdd = await fetch(`${BASE_URL}/wishlist_items/add_items_to_wishlist`, {
        method: "POST",
        headers: { "Content-Type": "application/json", },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      const data = await resAdd.json();

      if (!resAdd.ok) {
        setMessage(data.error || "Failed to add item");
        setMessageType("error");
        return;
      }

      if (data.already_exists) {
        setWishlistStatus((prev) => ({
          ...prev,
          [product.item_id]: "exists",
        }));
        setMessage(data.message || `${product.name} is already in wishlist`);
        setMessageType("info");

        return;
      }

      // success
      setWishlistStatus((prev) => ({
        ...prev,
        [product.item_id]: "added",
      }));
      setMessage(data.message || `${product.name} added to wishlist!`);
      setMessageType("success");

    } catch (err) {
      setMessage("Something went wrong");
      setMessageType("error");
    }
  };


  // ---------------- Add to Cart ----------------
  const handleAddToCart = async (product) => {
    if (!user) {
      setMessage("Please login to add items to cart")
      setMessageType("error")
      navigate("/checkout/login");
      return;
    }

    try {
      // Fetch or create user cart
      let cartId;
      const resCart = await fetch(`${BASE_URL}/cart/fetch_cart_details?user_id=${user.user_id}`, {
        method: "GET",
        credentials: "include",
      });

      if (resCart.status === 404) {
        const createRes = await fetch(`${BASE_URL}/cart/create_cart`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ user_id: user.user_id }),
        });
        const data = await createRes.json();
        cartId = data.cart_id;
      } else {
        const data = await resCart.json();
        cartId = data.cart_id;
      }

      const imageUrl = product.images?.length
      ? `${BASE_URL}/download_images/${
          typeof product.images[0] === "object" ? product.images[0].id : product.images[0]
        }`
      : "/assets/default.jpg";

      const payload = {
        cart_id: Number(cartId),
        item_id: Number(product.item_id || product.id),
        quantity: 1,
        name: product.name,
        price: Number(product.price),
        image: imageUrl,
      };

      const resAdd = await fetch(`${BASE_URL}/cart_items/add_items_to_cart`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      if (!resAdd.ok) throw new Error("Failed to add item to cart");

      // window.dispatchEvent(new Event("cartUpdated"));
      window.dispatchEvent(new CustomEvent("cartUpdated", { detail: { user_id: user.user_id } }));
      // setMessage(`${product.name} added to wishlist!`)
      setMessageType("success")
    } catch (err) {
      setMessage(`Failed to add item to wishlist: ${err.message}`)
      setMessageType("error")
    }
  };

  // ---------------- Slider settings ----------------
  const settings = {
    dots: true,
    infinite: true,
    autoplay: true,
    autoplaySpeed: 3000,
    speed: 800,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: true,
    nextArrow: <NextArrow />,
    prevArrow: <PrevArrow />,
  };

  const banners = [
    "/assets/Drones.jpeg",
    "/assets/Drones_1.jpeg",
    "/assets/Drones_2.jpg",
  ];

  if (loading) return <div>Loading home...</div>;

  return (
    <>
      <section className="hero-section">
        <Slider {...settings}>
          {banners.map((src, index) => (
            <div key={index} className="hero-slide">
              <img src={src} alt={`Banner ${index + 1}`} className="hero-image" />
              <div className="hero-text">Top Drone Parts for Builders</div>
            </div>
          ))}
        </Slider>

        {message && (
          <div className={`global-msg ${messageType}`}>
            {/* {message} */}
          </div>
        )}

        <section className="key-features">
          <h2>Categories</h2>
          <div className="features">
            {categories.map((cat, idx) => (
              <div
                key={idx}
                className="feature-item"
                onClick={() => navigate(`/shop?category=${cat.name}`)}
              >
                <img src={cat.image} alt={cat.name} />
                <h3>{cat.name}</h3>
              </div>
            ))}
          </div>
        </section>

        <section className="features-Products">
          <h2>Featured Products</h2>
          <div className="feature">
            {featuredProducts.map((product) => (
              <div key={product.item_id} className="feature-items">
                {product.images?.length > 0 && (
                  <img
                    src={`${BASE_URL}/download_images/${
                      typeof product.images[0] === "object"
                        ? product.images[0].id
                        : product.images[0]
                        
                    }`}
                    alt={product.name}
                    onClick={() => navigate(`/product/${product.item_id}`)}
                    style={{ cursor: "pointer" }}
                  />
                )}
                <h3
                  data-fullname={product.name}
                  onClick={() => navigate(`/product/${product.item_id}`)}
                  style={{ cursor: "pointer" }}
                >
                  {product.name}
                </h3>
                <div className="star-rating">
                  {[...Array(5)].map((_, i) => (
                    <span
                      key={i}
                      className={`fa fa-star ${i < (product.rating || 4) ? "checked" : ""}`}
                    ></span>
                  ))}
                  <span>(0)</span>
                </div>
                <p className="pricing">₹ {product.price} (Excl.GST)</p>
                {product.quantity > 0 && (
                  <button className="add-to-cart" onClick={() => handleAddToCart(product)}>
                      Add to Cart
                  </button>
                )}
                <button onClick={() => handleAddToWishlist(product)}>
                    {wishlistStatus[product.item_id] === "added"
                    ? "Added to Wishlist"
                    : wishlistStatus[product.item_id] === "exists"
                    ? "Already in Wishlist"
                    : "Add to Wishlist"}
                </button>
              </div>
            ))}
          </div>
        </section>

      </section>
      <Footer />
    </>
  );
};

export default Home;